import React from "react";
import { Grid } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import i18n from "../../../../i18n";
import TikTokIcon from "../../../../components/icons/TikTokIcon";
import type {
  PodcastMediaKit,
  YoutubeGeoCityRow,
  YoutubeGeoCountryRow,
} from "../../../../api/podcasts";
import { DemographicsBarsTable } from "../DemographicsBarsTable";
import type { DemographicsTableGroup } from "../metricsTables";
import type { MetricsTableColumn } from "../MetricsComparisonTable";

type YtAudience = NonNullable<
  NonNullable<
    NonNullable<PodcastMediaKit["analytics"]>["youtube"]
  >["longForm"]
>["audience"];

const countryDisplayNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["es"], { type: "region" })
    : null;

function formatCountryLabel(code: string): string {
  const upper = code.trim().toUpperCase();
  if (!upper) return code;
  try {
    return countryDisplayNames?.of(upper) ?? upper;
  } catch {
    return upper;
  }
}

type GeoBarRow = { key: string; label: string; sharePercent: number };

const GEO_TOP_N = 9;

function topNinePlusRest<T extends { sharePercent: number }>(
  rows: T[],
  toBar: (row: T, index: number) => GeoBarRow,
  restLabel: string,
  restKey: string,
): GeoBarRow[] {
  const sorted = [...rows].sort((a, b) => b.sharePercent - a.sharePercent);
  const top = sorted.slice(0, GEO_TOP_N).map((row, index) => toBar(row, index));
  const rest = sorted.slice(GEO_TOP_N);
  if (rest.length === 0) return top;
  const restShare = rest.reduce((sum, row) => sum + row.sharePercent, 0);
  return [
    ...top,
    {
      key: restKey,
      label: restLabel,
      sharePercent: Math.round(restShare * 100) / 100,
    },
  ];
}

export function buildCountryBars(countries: YoutubeGeoCountryRow[]): GeoBarRow[] {
  return topNinePlusRest(
    countries,
    (country) => ({
      key: country.code,
      label: formatCountryLabel(country.code),
      sharePercent: country.sharePercent,
    }),
    i18n.t("miContenido:metrics.restCountries"),
    "rest-countries",
  );
}

export function buildCityBars(cities: YoutubeGeoCityRow[]): GeoBarRow[] {
  return topNinePlusRest(
    cities,
    (city, index) => ({
      key: `${city.name}-${index}`,
      label: city.name,
      sharePercent: city.sharePercent,
    }),
    i18n.t("miContenido:metrics.restCities"),
    "rest-cities",
  );
}

const GEO_BAR_COLORS = [
  "#667eea",
  "#764ba2",
  "#f093fb",
  "#4facfe",
  "#43e97b",
  "#fa709a",
  "#fee140",
];

function geoBarColor(idx: number): string {
  return GEO_BAR_COLORS[idx % GEO_BAR_COLORS.length] ?? "#667eea";
}

export const youtubeIcon = (
  <YouTubeIcon sx={{ fontSize: 24, color: "#FF0000" }} />
);
export const instagramIcon = (
  <InstagramIcon sx={{ fontSize: 24, color: "#E1306C" }} />
);
export const tiktokIcon = <TikTokIcon sx={{ fontSize: 22 }} />;
export const facebookIcon = (
  <FacebookIcon sx={{ fontSize: 22, color: "#1877F2" }} />
);

const YOUTUBE_GEO_COLUMN: MetricsTableColumn[] = [
  { id: "youtube", label: "YouTube", icon: youtubeIcon },
];

function buildGeoGroup(
  key: string,
  label: string,
  bars: GeoBarRow[],
  colorOffset: number,
): DemographicsTableGroup | null {
  if (bars.length === 0) return null;
  return {
    key,
    label,
    rows: bars.map((item, idx) => ({
      key: item.key,
      label: item.label,
      values: [item.sharePercent],
      color: geoBarColor(idx + colorOffset),
    })),
  };
}

export function YouTubeGeoCard({
  audience,
}: {
  audience: NonNullable<YtAudience>;
}) {
  const countriesGroup = buildGeoGroup(
    "countries",
    i18n.t("miContenido:metrics.countries"),
    buildCountryBars(audience.countries ?? []),
    0,
  );
  const citiesGroup = buildGeoGroup(
    "cities",
    i18n.t("miContenido:metrics.cities"),
    buildCityBars(audience.cities ?? []),
    3,
  );

  if (!countriesGroup && !citiesGroup) return null;

  const twoCol = Boolean(countriesGroup && citiesGroup);

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {countriesGroup && (
        <Grid size={{ xs: 12, md: twoCol ? 6 : 12 }}>
          <DemographicsBarsTable
            columns={YOUTUBE_GEO_COLUMN}
            groups={[countriesGroup]}
          />
        </Grid>
      )}
      {citiesGroup && (
        <Grid size={{ xs: 12, md: twoCol ? 6 : 12 }}>
          <DemographicsBarsTable
            columns={YOUTUBE_GEO_COLUMN}
            groups={[citiesGroup]}
          />
        </Grid>
      )}
    </Grid>
  );
}
